import React from 'react';
import {
  Box, Container, Typography, Grid, Card, CardContent, Button,
  Stack, Paper, Chip
} from '@mui/material';
import {
  Storage as StorageIcon,
  Security as SecurityIcon,
  Schedule as ScheduleIcon,
  LocalShipping as ShippingIcon,
  ArrowForward as ArrowIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const StorageCrossSell = ({ context = 'moving' }) => {
  const navigate = useNavigate();

  const benefits = [
    {
      icon: <SecurityIcon />,
      title: '24/7 Security',
      description: 'Climate-controlled units with professional monitoring'
    },
    {
      icon: <ScheduleIcon />,
      title: 'Flexible Access',
      description: 'Access your items whenever you need them'
    },
    {
      icon: <ShippingIcon />,
      title: 'Moving Integration',
      description: 'Seamless coordination with your move'
    }
  ];

  const getContextualContent = () => {
    switch (context) {
      case 'quote':
        return {
          title: 'Need Storage During Your Move?',
          subtitle: 'Many customers need temporary storage during transitions. Save money by booking storage along with your move.',
          cta: 'Add Storage to My Move',
          discount: '15% off when combined with moving services'
        };
      case 'booking':
        return {
          title: 'Protect Your Items with Storage',
          subtitle: 'Keep valuable items safe during renovation or while staging your home for sale.',
          cta: 'Browse Storage Options',
          discount: '10% off first month storage'
        };
      default:
        return {
          title: 'Complete Storage Solutions',
          subtitle: 'From moving day storage to long-term solutions, we make storage simple and secure.',
          cta: 'Explore Storage Units',
          discount: 'Starting at just $79/month'
        };
    }
  };

  const content = getContextualContent();

  return (
    <Paper 
      elevation={3} 
      sx={{ 
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        borderRadius: 3,
        overflow: 'hidden',
        position: 'relative'
      }}
    >
      {/* Background Pattern */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: 200,
          height: 200,
          opacity: 0.1,
          background: 'radial-gradient(circle, #3b82f6 2px, transparent 2px)',
          backgroundSize: '20px 20px'
        }}
      />
      
      <Container maxWidth="lg" sx={{ py: 6, position: 'relative', zIndex: 1 }}>
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={8}>
            <Stack spacing={3}>
              <Box>
                <Stack direction="row" alignItems="center" spacing={2} mb={2}>
                  <StorageIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                  <Chip 
                    label={content.discount} 
                    color="secondary" 
                    sx={{ fontWeight: 700 }} 
                  />
                </Stack>
                
                <Typography variant="h4" fontWeight={800} gutterBottom>
                  {content.title}
                </Typography>
                
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                  {content.subtitle}
                </Typography>
              </Box>

              <Grid container spacing={3}>
                {benefits.map((benefit, index) => (
                  <Grid item xs={12} sm={4} key={index}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Box sx={{ color: 'primary.main' }}>
                        {benefit.icon}
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" fontWeight={700}>
                          {benefit.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {benefit.description}
                        </Typography>
                      </Box>
                    </Stack>
                  </Grid>
                ))}
              </Grid>
            </Stack>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box textAlign="center">
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowIcon />}
                onClick={() => navigate('/storage-marketplace')}
                sx={{
                  py: 2,
                  px: 4,
                  fontWeight: 700,
                  borderRadius: 3,
                  fontSize: '1.1rem',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
                    transform: 'translateY(-2px)'
                  },
                  transition: 'all 0.2s ease'
                }}
              >
                {content.cta}
              </Button>
              
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Browse 500+ storage units across Canada
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Paper>
  );
};

export default StorageCrossSell;